import React, { useContext, useState, useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import { LockOutlined, StarOutlined, LoginOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthContext } from '../../common/constants';
import { UserService } from '../../common/services';
import { useNavigate } from 'react-router-dom';
import { SESSION_EXPIRED_KEY, SESSION_RETURN_URL } from '../../common/utils/APIUtils';
import { useVersionCheck } from '../../common/hooks';
import { useT } from '../../common/i18n';
import logo from '../../assets/logo/logo-cap-assurances.JPG';
import './login.page.scss';

const Login = () => {
    const auth = useContext(AuthContext);
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const t = useT();
    // Évite de se reconnecter sur une version périmée après un déploiement
    useVersionCheck();

    useEffect(() => {
        try {
            if (sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1') {
                sessionStorage.removeItem(SESSION_EXPIRED_KEY);
                toast.warning(t('auth.session.expired'), {
                    position: 'top-right',
                    autoClose: 6000,
                });
            }
        } catch { /* */ }
    }, [t]);

    const handleLogin = async (values) => {
        try {
            setLoading(true);
            const formattedData = {
                phone: `+225${values.phone}`,
                password: values.password,
            };
            const response = await UserService.signin(formattedData);
            // Redirect vers l'URL initiale si elle existait avant l'expiration
            let returnUrl = null;
            try {
                returnUrl = sessionStorage.getItem(SESSION_RETURN_URL);
                if (returnUrl) sessionStorage.removeItem(SESSION_RETURN_URL);
            } catch { /* */ }
            auth.onLogin(response, returnUrl ? (path) => navigate(returnUrl || path) : navigate);
        } catch (error) {
            const errorMessage = error.message || 'Une erreur est survenue lors de la connexion.';
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 5000,
            });
        } finally {
            setLoading(false);
        }
    };

    const features = [
        'Gestion complete des contrats',
        'Suivi des sinistres',
        'Tableau de bord analytique',
        'Administration securisee',
    ];

    return (
        <>
            <ToastContainer />
            <div className="login-container">
                {/* Panel Gauche - Branding */}
                <div className="login-branding">
                    <div className="branding-content">
                        <div className="logo-container">
                            <img src={logo} alt="Cap Assurances" className="logo-img" />
                        </div>

                        <h1>{t('common.app_name').toUpperCase()}</h1>
                        <p className="tagline">{t('auth.login.broker_tagline')}</p>

                        <ul className="features">
                            {features.map((feature, index) => (
                                <li key={index}>
                                    <span className="feature-icon">
                                        <StarOutlined />
                                    </span>
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <footer className="branding-footer">
                        &copy; {new Date().getFullYear()} Cap Assurances. Tous droits reserves.
                    </footer>
                </div>

                {/* Panel Droit - Formulaire */}
                <div className="login-form-panel">
                    <div className="login-form-container">
                        <div className="login-header">
                            <h2>Bienvenue !</h2>
                            <p>Connectez-vous à votre espace administrateur</p>
                        </div>

                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleLogin}
                            className="login-form"
                            autoComplete="off"
                        >
                            <Form.Item
                                label={t('auth.login.phone')}
                                name="phone"
                                rules={[
                                    { required: true, message: t('error.required', { field: t('auth.login.phone').toLowerCase() }) },
                                    { pattern: /^\d+$/, message: t('error.phone_format') }
                                ]}
                            >
                                <div className="phone-input-group">
                                    <div className="country-prefix">
                                        <span className="flag"></span>
                                        <span>+225</span>
                                    </div>
                                    <Input
                                        placeholder=""
                                        size="large"
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            form.setFieldsValue({ phone: value });
                                        }}
                                    />
                                </div>
                            </Form.Item>

                            <Form.Item
                                label={t('auth.login.password')}
                                name="password"
                                rules={[{ required: true, message: t('error.required', { field: t('auth.login.password').toLowerCase() }) }]}
                                className="password-input"
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="********"
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={isLoading}
                                    className="login-button"
                                    icon={<LoginOutlined />}
                                >
                                    {isLoading ? t('auth.login.loading') : t('auth.login.submit')}
                                </Button>
                            </Form.Item>

                            <div className="ssl-security">
                                <SafetyCertificateOutlined />
                                <span>Connexion sécurisée avec chiffrement SSL</span>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
